import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Database, RefreshCw, AlertCircle, CheckCircle2, AlertTriangle, Mail, Clock, Download } from "lucide-react";
import { toast } from "sonner";
import RestorePanel from "./RestorePanel";

export default function BackupsAndDunning() {
  const [backups, setBackups] = useState([]);
  const [dunning, setDunning] = useState([]);
  const [busy, setBusy] = useState(false);
  // Optional date-range filter for the SQL Server bundle export — default to
  // the last 30 days. Computed lazily inside the initial state callbacks so
  // it doesn't re-run every render.
  const [fromDate, setFromDate] = useState(() => new Date(Date.now() - 30 * 86400e3).toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10));
  // Client-wise export — list of hospitals + selected one.
  const [hospitals, setHospitals] = useState([]);
  const [hospitalId, setHospitalId] = useState("");

  const load = async () => {
    try {
      const [b, d, h] = await Promise.all([
        api.get("/superadmin/backups"),
        api.get("/payments/admin/dunning-log"),
        api.get("/superadmin/hospitals").catch(() => ({ data: { items: [] } })),
      ]);
      setBackups(b.data.items || []);
      setDunning(d.data.items || []);
      setHospitals(h.data.items || []);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const runBackup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/superadmin/backups/run-now");
      toast.success(`Backup OK · ${data.documents_total} docs · ${(data.bytes_total / 1024 / 1024).toFixed(2)} MB`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const runDunning = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/admin/run-dunning");
      toast.success(`Dunning scan complete · sent=${data.sent} · skipped=${data.skipped}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // Download a backup folder as a zip. We hit the API with axios so the bearer
  // token (set globally by `api`) flows through, then save the blob locally.
  const downloadBackup = async (backupDate) => {
    try {
      const res = await api.get(`/superadmin/backups/${backupDate}/download`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `careo-backup-${backupDate}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Backup ${backupDate} downloaded`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Download failed");
    }
  };

  // Live full-DB export → one JSON array per collection inside a zip.
  // Designed for importing into MS SQL (OPENJSON / SSIS) — slower than the
  // nightly backup but always reflects current state.
  const exportDbJson = async () => {
    setBusy(true);
    toast.info("Preparing full-database JSON export — this may take a moment…");
    try {
      const res = await api.get("/superadmin/db-export/json", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const stamp = new Date().toISOString().slice(0, 16).replace(/[T:]/g, "-");
      const a = document.createElement("a");
      a.href = url;
      a.download = `careo-db-export-${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Database export downloaded — ready to import into MS SQL");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Export failed");
    } finally { setBusy(false); }
  };

  // ⭐ The "best" path: complete SQL Server migration bundle —
  // schema + import scripts + data + README. The DBA just opens SSMS and
  // runs two SQL files. Recommended over the plain JSON export.
  // `mode` decides which filters get attached: full / dated / client / client+dated.
  const exportSqlServerBundle = async (mode = "full") => {
    if (mode.includes("client") && !hospitalId) {
      toast.error("Pick a hospital first");
      return;
    }
    setBusy(true);
    const params = {};
    if (mode.includes("dated")) { params.from_date = fromDate; params.to_date = toDate; }
    if (mode.includes("client")) { params.hospital_id = hospitalId; }
    const hcode = hospitals.find(h => h.id === hospitalId)?.code || "";
    toast.info(`Building SQL Server bundle${mode === "full" ? "" : ` (${mode}${hcode ? " · " + hcode : ""})`}…`);
    try {
      const res = await api.get("/superadmin/db-export/sqlserver", { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const stamp = new Date().toISOString().slice(0, 16).replace(/[T:]/g, "-");
      const bits = [];
      if (params.hospital_id && hcode) bits.push(hcode);
      if (params.from_date && params.to_date) bits.push(`${params.from_date}_to_${params.to_date}`);
      const suffix = bits.length ? `-${bits.join("-")}` : "";
      const a = document.createElement("a");
      a.href = url;
      a.download = `careo-sqlserver-migration${suffix}-${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("SQL Server bundle downloaded — see README.md inside");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Export failed");
    } finally { setBusy(false); }
  };

  const fmtBytes = (b) => {
    if (!b) return "—";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(2)} MB`;
  };
  const fmtTime = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—";

  return (
    <div className="space-y-6" data-testid="backups-dunning-page">
      <div>
        <h1 className="font-heading text-3xl font-semibold">Backups & Dunning</h1>
        <p className="mt-1 text-sm text-ink-muted">Daily MongoDB snapshots (30-day retention) and post-expiry dunning emails for overdue subscriptions.</p>
      </div>

      {/* Backups */}
      <Card className="overflow-hidden">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div className="flex items-center gap-2">
            <Database size={16} className="text-brand" />
            <div>
              <div className="font-heading text-base font-semibold">MongoDB backup history</div>
              <div className="text-xs text-ink-muted">Auto-runs once every 24 hours · Files saved under <code>/app/backups/YYYY-MM-DD/</code></div>
            </div>
          </div>
          <Button onClick={runBackup} disabled={busy} className="bg-brand hover:bg-brand-700" data-testid="backup-run-now">
            <RefreshCw size={14} className={`mr-2 ${busy ? "animate-spin" : ""}`} />Run backup now
          </Button>
        </div>

        {/* MS-SQL-friendly export options — recommended path on top */}
        <div className="border-b border-border bg-emerald-50/40 px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-start gap-2">
              <Database size={14} className="mt-0.5 text-emerald-700" />
              <div className="text-[12px]">
                <div><b>SQL Server migration bundle</b> · ⭐ recommended for MS SQL import</div>
                <div className="text-ink-muted">Includes auto-generated <code>CREATE TABLE</code> schema, <code>OPENJSON BULK INSERT</code> scripts, every collection as a JSON array, and a step-by-step README. Open the README inside the zip and follow 3 clicks.</div>
              </div>
            </div>
            <Button size="sm" onClick={() => exportSqlServerBundle("full")} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="db-export-sqlserver-btn">
              <Download size={12} className="mr-1" />Export → SQL Server
            </Button>
          </div>

          {/* Date-range filter — only filters collections that have created_at. */}
          <div className="mt-3 flex flex-wrap items-end gap-2 rounded border border-emerald-200 bg-white px-3 py-2">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted">From</div>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="rounded border border-border bg-white px-2 py-1 text-xs" data-testid="db-export-from-date" />
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted">To</div>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="rounded border border-border bg-white px-2 py-1 text-xs" data-testid="db-export-to-date" />
            </div>
            <Button size="sm" onClick={() => exportSqlServerBundle("dated")} disabled={busy || !fromDate || !toDate} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="db-export-sqlserver-dated-btn">
              <Download size={12} className="mr-1" />Export this date range
            </Button>
            <span className="text-[10px] text-ink-muted">Filters by <code>created_at</code>. Masters / counters without a date are exported in full.</span>
          </div>

          {/* Client-wise export — one hospital at a time. */}
          <div className="mt-3 flex flex-wrap items-end gap-2 rounded border border-emerald-300 bg-white px-3 py-2">
            <div className="min-w-[220px]">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted">Hospital (client)</div>
              <select
                value={hospitalId}
                onChange={(e) => setHospitalId(e.target.value)}
                className="rounded border border-border bg-white px-2 py-1 text-xs w-full"
                data-testid="db-export-hospital-select"
              >
                <option value="">— select hospital —</option>
                {hospitals.map(h => (
                  <option key={h.id} value={h.id}>{h.code ? `${h.code} · ` : ""}{h.name}</option>
                ))}
              </select>
            </div>
            <Button size="sm" onClick={() => exportSqlServerBundle("client")} disabled={busy || !hospitalId} className="bg-indigo-700 hover:bg-indigo-800 text-white" data-testid="db-export-client-btn">
              <Download size={12} className="mr-1" />Export this client (full)
            </Button>
            <Button size="sm" onClick={() => exportSqlServerBundle("client+dated")} disabled={busy || !hospitalId || !fromDate || !toDate} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="db-export-client-dated-btn">
              <Download size={12} className="mr-1" />Export this client · date range
            </Button>
            <span className="text-[10px] text-ink-muted">Per-tenant snapshot for handing off to a single hospital&apos;s IT team.</span>
          </div>

          <div className="mt-2 flex items-center justify-between rounded border border-emerald-200 bg-white px-2 py-1.5 text-[11px]">
            <span className="text-ink-muted">Just need raw JSON (no schema/scripts)? Use the plain export.</span>
            <Button size="sm" variant="outline" onClick={exportDbJson} disabled={busy} className="h-7 border-emerald-300 text-emerald-800 hover:bg-emerald-50" data-testid="db-export-json-btn">
              <Download size={11} className="mr-1" />JSON only
            </Button>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-wider text-ink-muted">
            <tr>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-right">Collections</th>
              <th className="px-3 py-2 text-right">Documents</th>
              <th className="px-3 py-2 text-right">Size</th>
              <th className="px-3 py-2 text-right">Duration</th>
              <th className="px-3 py-2 text-left">Triggered by</th>
              <th className="px-3 py-2 text-left">Started</th>
              <th className="px-3 py-2 text-right">Download</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {backups.length === 0 && <tr><td colSpan="9" className="px-3 py-10 text-center text-ink-muted">No backups yet. Click <b>Run backup now</b> to create the first one.</td></tr>}
            {backups.map((b) => (
              <tr key={b.id} data-testid={`backup-row-${b.backup_date}`}>
                <td className="px-3 py-2 font-medium">{b.backup_date}</td>
                <td className="px-3 py-2">
                  {b.status === "success"
                    ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700"><CheckCircle2 size={11} />Success</span>
                    : b.status === "partial"
                      ? <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700"><AlertTriangle size={11} />Partial</span>
                      : <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-semibold text-rose-700"><AlertCircle size={11} />Failed</span>}
                </td>
                <td className="px-3 py-2 text-right">{b.collections_count}</td>
                <td className="px-3 py-2 text-right">{b.documents_total?.toLocaleString("en-IN") || 0}</td>
                <td className="px-3 py-2 text-right">{fmtBytes(b.bytes_total)}</td>
                <td className="px-3 py-2 text-right">{b.duration_sec}s</td>
                <td className="px-3 py-2 text-[11px] text-ink-muted">{b.triggered_by}</td>
                <td className="px-3 py-2 text-[11px] text-ink-muted">{fmtTime(b.started_at)}</td>
                <td className="px-3 py-2 text-right">
                  {b.status === "failed" ? (
                    <span className="text-[10px] text-ink-muted">—</span>
                  ) : (
                    <Button
                      size="sm" variant="outline" className="h-7 px-2"
                      onClick={() => downloadBackup(b.backup_date)}
                      data-testid={`backup-download-${b.backup_date}`}
                    >
                      <Download size={12} className="mr-1" />ZIP
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Restore */}
      <RestorePanel hospitals={hospitals} />

      {/* Dunning */}
      <Card className="overflow-hidden">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div className="flex items-center gap-2">
            <Mail size={16} className="text-rose-600" />
            <div>
              <div className="font-heading text-base font-semibold">Post-expiry dunning emails</div>
              <div className="text-xs text-ink-muted">Sent at <b>+3 · +7 · +14 · +21 · +28</b> days overdue · idempotent per (hospital, validity, window)</div>
            </div>
          </div>
          <Button onClick={runDunning} disabled={busy} variant="outline" className="border-rose-200 text-rose-700 hover:bg-rose-50" data-testid="dunning-run-now">
            <Clock size={14} className={`mr-2 ${busy ? "animate-spin" : ""}`} />Run dunning scan now
          </Button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-wider text-ink-muted">
            <tr>
              <th className="px-3 py-2 text-left">Hospital</th>
              <th className="px-3 py-2 text-right">Days overdue</th>
              <th className="px-3 py-2 text-right">Attempt</th>
              <th className="px-3 py-2 text-left">Window</th>
              <th className="px-3 py-2 text-left">Final?</th>
              <th className="px-3 py-2 text-left">Recipients</th>
              <th className="px-3 py-2 text-left">Sent at</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {dunning.length === 0 && <tr><td colSpan="7" className="px-3 py-10 text-center text-ink-muted">No dunning emails sent yet. They are auto-sent at +3 / +7 / +14 / +21 / +28 days after subscription expiry.</td></tr>}
            {dunning.map((i) => (
              <tr key={i.key} data-testid={`dunning-row-${i.key}`}>
                <td className="px-3 py-2 font-mono text-[11px]">{i.hospital_id?.slice(0, 12)}…</td>
                <td className="px-3 py-2 text-right font-semibold text-rose-700">{i.days_overdue}</td>
                <td className="px-3 py-2 text-right">{i.attempt}/5</td>
                <td className="px-3 py-2 text-[11px]">+{i.window} days</td>
                <td className="px-3 py-2 text-[11px]">{i.final ? <span className="font-semibold text-rose-700">Final</span> : "—"}</td>
                <td className="px-3 py-2 text-[11px] text-ink-muted">{(i.recipients || []).join(", ")}</td>
                <td className="px-3 py-2 text-[11px] text-ink-muted">{fmtTime(i.sent_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
